import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Receipt,
  FileCheck,
  TrendingUp,
  Target,
  AlertTriangle,
  Globe,
  ArrowRight,
  CreditCard,
  Calculator // Billing Dashboard icon
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const FinanceLanding = () => {
  const navigate = useNavigate();

  const financeCards = [
    {
      id: 'asset-finance',
      title: 'Asset Finance',
      description: 'Capitalization, depreciation & valuation management',
      icon: <DollarSign size={24} />,
      path: '/finance/assets',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Capitalization', 'Depreciation', 'Valuation']
    },
    {
      id: 'contract-finance',
      title: 'Contract Finance',
      description: 'Payment terms, AP/AR & revenue recognition',
      icon: <Receipt size={24} />,
      path: '/finance/contracts',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Payment terms', 'AP/AR', 'Revenue recognition']
    },
    // New Payment Management Card
    {
      id: 'payment-management',
      title: 'Payment Management',
      description: 'Track milestones, recurring payments & invoice status',
      icon: <CreditCard size={24} />,
      path: '/finance/payments',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Milestone tracking', 'Recurring payments', 'Status updates']
    },
    {
      id: 'billing-dashboard',
      title: 'Billing Dashboard',
      description: 'Comprehensive billing and payment tracking',
      icon: <Calculator size={24} />,
      path: '/finance/billing-dashboard',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Payment terms', 'AP/AR', 'Revenue recognition']
    },
    {
      id: 'invoice-validation',
      title: 'Invoice Validation',
      description: 'AI-OCR automated validation & processing',
      icon: <FileCheck size={24} />,
      path: '/finance/invoices',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['AI-OCR', 'Automated validation', 'Processing']
    },
    {
      id: 'cash-flow-forecasting',
      title: 'Cash Flow Forecasting',
      description: 'AI predictive cash flow analysis & planning',
      icon: <TrendingUp size={24} />,
      path: '/finance/cashflow',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['AI predictions', 'Cash flow analysis', 'Planning']
    },
    {
      id: 'spend-optimization',
      title: 'Spend Optimization',
      description: 'AI spend analysis & cost optimization recommendations',
      icon: <Target size={24} />,
      path: '/finance/optimization',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Spend analysis', 'Cost optimization', 'AI recommendations']
    },
    {
      id: 'risk-exposure',
      title: 'Risk & Exposure',
      description: 'Financial risk & exposure analysis',
      icon: <AlertTriangle size={24} />,
      path: '/finance/risk',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Risk analysis', 'Exposure tracking', 'Compliance']
    },
    {
      id: 'multi-currency',
      title: 'Multi-Currency',
      description: 'Global contracts & tax compliance management',
      icon: <Globe size={24} />,
      path: '/finance/currency',
      iconBg: '#D6EFFF',
      iconLine: 'var(--gold-primary)',
      features: ['Global contracts', 'Tax compliance', 'Multi-currency']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Finance Management"
        description="Comprehensive financial management and optimization system"
      />
      <div className="page-container">
        {/* Finance Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {financeCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg || '#FFF9E6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: card.iconLine || 'var(--gold-primary)' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default FinanceLanding;